import React from "react";
import { Button, Card, Tag, Spin, message } from "antd";
import { CrownOutlined } from "@ant-design/icons";
import { defineMessages, useIntl } from "react-intl";
import { useSubscription } from "../subscription";
import { PRICING } from "../subscription/types";
import { API } from "../apiUrl";
import "./Settings.sass";

const messages = defineMessages({
  freePlan: { id: "sub.freePlan", defaultMessage: "Free Plan" },
  freePlanDesc: { id: "sub.freePlanDesc", defaultMessage: "Unlock unlimited downloads and premium features. Subscribe now!" },
  monthly: { id: "sub.monthly", defaultMessage: "Monthly" },
  perMonth: { id: "sub.perMonth", defaultMessage: "per month" },
  yearly: { id: "sub.yearly", defaultMessage: "Yearly" },
  perYear: { id: "sub.perYear", defaultMessage: "per year" },
  subscribe: { id: "sub.subscribe", defaultMessage: "Subscribe" },
  subscribed: { id: "sub.subscribed", defaultMessage: "Subscribed" },
  subscribedStatus: { id: "sub.subscribedStatus", defaultMessage: "You are already subscribed to Premium" },
  subscribedSuccess: { id: "sub.subscribedSuccess", defaultMessage: "Subscription Successful" },
  premiumUntil: { id: "premium.until", defaultMessage: "Premium until {date}" },
  savePercent: { id: "sub.savePercent", defaultMessage: "Save {percent}%" },
  termsText: { id: "sub.termsText", defaultMessage: "By subscribing you agree to our {terms} and {privacy}. Payments are non-refundable. Premium is for personal use only. Accounts exceeding 30 unique IPs and 1,000 downloads per day may be suspended." },
  checkoutFailed: { id: "sub.checkoutFailed", defaultMessage: "Failed to create checkout" },
  checkoutError: { id: "sub.checkoutError", defaultMessage: "Unable to start checkout. Please try again." },
  unavailable: { id: "sub.unavailable", defaultMessage: "Pricing is temporarily unavailable. Please try again later." },
});

interface ProductInfo {
  id: string;
  name: string;
  price: number | null;
  currency: string;
  interval: "month" | "year";
}

const isLocal = typeof window !== "undefined" && window.location.hostname === "localhost";

interface Props {
  onRequireLogin?: (plan: "month" | "year") => void;
  onCheckout?: (plan: "month" | "year") => void;
  checkoutLoading?: boolean;
  premiumExpiresAt?: number | null;
  premiumTier?: string;
  isLoggedIn?: boolean;
}

const SubscriptionPanel: React.FC<Props> = ({ onRequireLogin, onCheckout, checkoutLoading, premiumExpiresAt, premiumTier, isLoggedIn }) => {
  const intl = useIntl();
  const { isPremium: contextPremium } = useSubscription();
  const isPremium = contextPremium || premiumTier === "premium";
  const expiryDate = premiumExpiresAt
    ? new Date(premiumExpiresAt * 1000).toLocaleDateString()
    : null;
  const [products, setProducts] = React.useState<ProductInfo[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [fetchError, setFetchError] = React.useState(false);

  React.useEffect(() => {
    fetch(`${API}/api/products`)
      .then((r) => r.json())
      .then((data) => {
        if (data.products) setProducts(data.products);
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false));
  }, []);

  const monthly = products.find((p) => p.interval === "month");
  const yearly = products.find((p) => p.interval === "year");

  // Dev (localhost): always use static PRICING for consistent local development
  // Production: only use API prices; if unavailable, show unavailable state
  const hasApiPrices = monthly?.price != null && yearly?.price != null;
  const pricesReady = isLocal || hasApiPrices;
  const unavailable = !isLocal && !loading && !hasApiPrices;

  const monthlyPrice = isLocal ? PRICING.monthly.amount : (hasApiPrices ? monthly!.price! : 0);
  const yearlyPrice = isLocal ? PRICING.yearly.amount : (hasApiPrices ? yearly!.price! : 0);

  const handleUpgrade = (plan: "month" | "year") => {
    if (!isLoggedIn) {
      if (onRequireLogin) {
        onRequireLogin(plan);
      } else {
        message.error(intl.formatMessage({ id: "sub.loginRequired", defaultMessage: "Please sign in first to subscribe." }));
      }
      return;
    }
    if (onCheckout) {
      onCheckout(plan);
    }
  };

  const savePercent = pricesReady
    ? Math.round((1 - yearlyPrice / (monthlyPrice * 12)) * 100)
    : 0;

  return (
    <div className="Settings__panel">
      {isPremium && (
        <Card className="Settings__panelCard Settings__panelCard--subscribed">
          <CrownOutlined className="Settings__panelIcon" />
          <h5 className="Settings__panelTitle">{intl.formatMessage(messages.subscribedSuccess)}</h5>
          {expiryDate && (
            <span className="Settings__panelExpiry">
              {intl.formatMessage(messages.premiumUntil, { date: expiryDate })}
            </span>
          )}
          <Tag color="gold" className="Settings__panelTag">{intl.formatMessage(messages.subscribed)}</Tag>
        </Card>
      )}

      {!isPremium && (
        <Card className="Settings__panelCard Settings__panelCard--free">
          <CrownOutlined className="Settings__panelIcon--muted" />
          <h5 className="Settings__panelTitle">{intl.formatMessage(messages.freePlan)}</h5>
          <span className="Settings__panelDesc" style={{ marginBottom: 0 }}>
            {intl.formatMessage(messages.freePlanDesc)}
          </span>
        </Card>
      )}

      {loading ? (
        <div className="Settings__loading"><Spin /></div>
      ) : unavailable ? (
        <div className="Settings__unavailable">
          <p>{intl.formatMessage(messages.unavailable)}</p>
        </div>
      ) : (
        <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
          <Card
            hoverable={!isPremium && !checkoutLoading}
            onClick={isPremium || checkoutLoading ? undefined : () => handleUpgrade("month")}
            className={`Settings__panelCard Settings__panelCard--pricing${isPremium || checkoutLoading ? " Settings__panelCard--disabled" : ""}`}
          >
            <p className="Settings__planName">{intl.formatMessage(messages.monthly)}</p>
            <h4 className="Settings__planPrice">${monthlyPrice}</h4>
            <p className="Settings__planInterval">{intl.formatMessage(messages.perMonth)}</p>
            <Button type="default" block className="Settings__planBtn" disabled={isPremium || checkoutLoading} loading={checkoutLoading}>
              {intl.formatMessage(messages.subscribe)}
            </Button>
          </Card>
          <Card
            hoverable={!isPremium && !checkoutLoading}
            onClick={isPremium || checkoutLoading ? undefined : () => handleUpgrade("year")}
            className={`Settings__panelCard Settings__panelCard--pricing Settings__panelCard--pricing--yearly${isPremium || checkoutLoading ? " Settings__panelCard--disabled" : ""}`}
          >
            {savePercent > 0 && (
              <Tag color="gold" className="Settings__planSaveTag">
                {intl.formatMessage(messages.savePercent, { percent: savePercent })}
              </Tag>
            )}
            <h4 className="Settings__planPrice">${yearlyPrice}</h4>
            <p className="Settings__planInterval">{intl.formatMessage(messages.perYear)}</p>
            <Button type="primary" block className="Settings__planBtn" disabled={isPremium || checkoutLoading} loading={checkoutLoading}>
              {intl.formatMessage(messages.subscribe)}
            </Button>
          </Card>
        </div>
      )}

      <div className="Settings__terms">
        <p>
          {intl.formatMessage(messages.termsText, {
            terms: <a href={API + "/terms"} target="_blank" rel="noreferrer">Terms of Service</a>,
            privacy: <a href={API + "/privacy"} target="_blank" rel="noreferrer">Privacy Policy</a>,
          })}
        </p>
      </div>
    </div>
  );
};

export default SubscriptionPanel;